import React, { useState, useEffect, use } from 'react';
import { addSubscriptionsToPlan, cancelSubscription, deleteSubscription, fetchSubscriptions, updateSubscriptionNotes, sendSubscriptionLink } from '../utils/adminSubscriptionApi';
import { createPlan, fetchPlan, fetchPlans, updatePlan } from '../utils/adminPlanApi';
import Header from '../components/Header';
import Button from '../components/elements/Button';
import Input from '../components/elements/Input';
import { CreditCard, Plus, RefreshCcw, ClipboardCopy, Trash, NotepadText, X, Edit, Save, Send } from 'lucide-react';
import Notification from '../components/Notification';
import Loading from '../components/elements/Loading';
import JsonDetailsModal from '../components/JsonDetailsModal';
import Switch from '../components/elements/Switch';
import Modal from '../components/Modal';
import { toDate, toSeconds } from '../utils/globalUtils';
import Layout from '../components/Layout';

export default function AdminPlansPage() {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [description, setDescription] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [startDateError, setStartDateError] = useState('');
  const [endDate, setEndDate] = useState(new Date(new Date().setDate(new Date().getDate() + 365)).toISOString().slice(0, 10));
  const [endDateError, setEndDateError] = useState('');
  const [status, setStatus] = useState('');
  const [notification, setNotification] = useState('');
  const [loading, setLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [editPlan, setEditPlan] = useState(null);
  const [addSubscriptionCount, setAddSubscriptionCount] = useState(1);
  const [copiedId, setCopiedId] = useState(null);
  const [isCustomPlan, setIsCustomPlan] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [originalSubscriptions, setOriginalSubscriptions] = useState([]);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [editingNotesValue, setEditingNotesValue] = useState('');
  const [sendLinkSubscriptionId, setSendLinkSubscriptionId] = useState(null);
  const [sendLinkEmail, setSendLinkEmail] = useState('');

  useEffect(() => {
    setNameError('');
  }, [name]);

  useEffect(() => {
    setStartDateError('');
  }, [startDate]);

  useEffect(() => {
    setEndDateError('');
  }, [endDate]);

  useEffect(() => {
    document.title = "Plans | Eternal Locker™";
    setLoading(true);
    fetchPlans()
      .then(data => {
        setPlans(data.plans || []);
        setLoading(false);
      }).catch(() => {
        setNotification({ message: 'Failed to fetch plans.', type: 'error' });
        setLoading(false);
      });
  }, []);

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setNotification({ message: 'Subscription ID copied to clipboard!', type: 'success' });
    setTimeout(() => setCopiedId(null), 3000);
  };

  const validateForm = () => {
    if (!name.trim()) {
      setNameError('Plan name is required.');
      setNotification({ message: 'Plan name is required.', type: 'error' });
      return false;
    }
    setNameError('');
    return true;
  };

  const validateSubscriptionForm = () => {
    if (!startDate) {
      setStartDateError('Start date is required.');
      setNotification({ message: 'Start date is required.', type: 'error' });
      return false;
    }
    if (!endDate) {
      setEndDateError('End date is required.');
      setNotification({ message: 'End date is required.', type: 'error' });
      return false;
    }
    if (new Date(endDate) <= new Date(startDate)) {
      setStartDateError('End date must be after start date.');
      setEndDateError('End date must be after start date.');
      setNotification({ message: 'End date must be after start date.', type: 'error' });
      return false;
    }
    if (new Date(endDate) < new Date()) {
      setEndDateError('End date must be in the future.');
      setNotification({ message: 'End date must be in the future.', type: 'error' });
      return false;
    }
    setStartDateError('');
    setEndDateError('');
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    if (editPlan) {
      setPlanLoading(true);
      updatePlan(editPlan.id, { name, description, internalNotes })
        .then(response => {
          if (response.success) {
            setNotification({ message: 'Plan updated successfully.', type: 'success' });
            setPlanLoading(false);
            setPlans(prevPlans => prevPlans.map(p => p.id === editPlan.id ? { ...p, name, description, internalNotes } : p));
          } else {
            setNotification({ message: response.message || 'Failed to update plan.', type: 'error' });
            setPlanLoading(false);
            return;
          }

        }).catch(() => {
          setNotification({ message: 'Failed to update plan.', type: 'error' });
          setPlanLoading(false);
        });
    } else {
      setPlanLoading(true);
      createPlan({ name, description, internalNotes })
        .then(response => {
          if (response.success) {
            setNotification({ message: 'Plan created successfully.', type: 'success' });
            const newPlan = response.plan;
            setPlans(prevPlans => [...prevPlans, newPlan]);
            setEditPlan(newPlan);
            setIsCustomPlan(newPlan.type === 'custom');
            setPlanLoading(false);
          } else {
            setNotification({ message: response.message || 'Failed to create plan.', type: 'error' });
            setPlanLoading(false);
          }
        }).catch(() => {
          setNotification({ message: 'Failed to create plan.', type: 'error' });
          setPlanLoading(false);
        });
    }
  }

  const handleAddNew = () => {
    setEditPlan(null);
    setName('');
    setDescription('');
    setInternalNotes('');
    setStartDate('');
    setEndDate('');
    setStatus('');
    setSubscriptions([]);
    setOriginalSubscriptions([]);
    setIsCustomPlan(false);
  }

  const handleEditPlan = (plan) => {
    setPlanLoading(true);
    setSubscriptions([]);
    setOriginalSubscriptions([]);
    setEditPlan(plan);
    setIsCustomPlan(plan.type === 'custom');
    setName(plan.name || '');
    setDescription(plan.description || '');
    setInternalNotes(plan.internalNotes || '');

    fetchSubscriptions(plan.id)
      .then(subData => {
        if (subData.success) {
          setSubscriptions(subData.subscriptions || []);
          setOriginalSubscriptions(subData.subscriptions || []);
          setPlanLoading(false);
        } else {
          setNotification({ message: subData.message || 'Failed to fetch subscriptions.', type: 'error' });
          setPlanLoading(false);
        }
      }).catch((err) => {
        setNotification({ message: 'Failed to fetch subscriptions.', type: 'error' });
        setPlanLoading(false);
      });
  }

  const handleAddSubscription = () => {
    if (!validateSubscriptionForm()) {
      return;
    }
    setPlanLoading(true);
    let count = parseInt(addSubscriptionCount, 10);
    if (isNaN(count) || count <= 0) {
      count = 0;
    }
    setAddSubscriptionCount(count);
    if (count <= 0) {
      setPlanLoading(false);
      return;
    }

    addSubscriptionsToPlan(editPlan.id, toSeconds(startDate), toSeconds(endDate), count)
      .then(response => {
        if (response.success) {
          setSubscriptions(prevSubs => [...prevSubs, ...response.subscriptions]);
          setOriginalSubscriptions(prevSubs => [...prevSubs, ...response.subscriptions]);
          setNotification({ message: `Added ${count} subscriptions successfully.`, type: 'success' });
          setPlanLoading(false);
        } else {
          setNotification({ message: response.message || 'Failed to add subscriptions.', type: 'error' });
          setPlanLoading(false);
        }
      })
      .catch(() => {
        setNotification({ message: 'Failed to add subscriptions.', type: 'error' });
        setPlanLoading(false);
      });
  }

  const handleDeleteSubscription = (subscriptionId) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    setPlanLoading(true);
    deleteSubscription(subscriptionId)
      .then(response => {
        if (response.success) {
          setSubscriptions(prevSubs => prevSubs.filter(s => s.subscriptionId !== subscriptionId));
          setOriginalSubscriptions(prevSubs => prevSubs.filter(s => s.subscriptionId !== subscriptionId));
          setNotification({ message: 'Subscription deleted successfully.', type: 'success' });
          setPlanLoading(false);
        } else {
          setNotification({ message: response.message || 'Failed to delete subscription.', type: 'error' });
          setPlanLoading(false);
        }
      })
      .catch(() => {
        setNotification({ message: 'Failed to delete subscription.', type: 'error' });
        setPlanLoading(false);
      });
  }

  const handleCancelSubscription = (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) {
      return;
    }

    setPlanLoading(true);
    cancelSubscription(subscriptionId)
      .then(response => {
        if (response.success) {
          setSubscriptions(prevSubs => prevSubs.map(s => s.subscriptionId === subscriptionId ? { ...s, status: 'cancelled' } : s));
          setOriginalSubscriptions(prevSubs => prevSubs.map(s => s.subscriptionId === subscriptionId ? { ...s, status: 'cancelled' } : s));
          setNotification({ message: 'Subscription cancelled successfully.', type: 'success' });
          setPlanLoading(false);
        } else {
          setNotification({ message: response.message || 'Failed to cancel subscription.', type: 'error' });
          setPlanLoading(false);
        }
      })
      .catch(() => {
        setNotification({ message: 'Failed to cancel subscription.', type: 'error' });
        setPlanLoading(false);
      });
  }

  const handleSaveNotes = (subscriptionId) => {
    setPlanLoading(true);
    updateSubscriptionNotes(subscriptionId, editingNotesValue)
      .then(response => {
        if (response.success) {
          setSubscriptions(prevSubs => prevSubs.map(s => s.subscriptionId === subscriptionId ? { ...s, internalNotes: editingNotesValue } : s));
          setOriginalSubscriptions(prevSubs => prevSubs.map(s => s.subscriptionId === subscriptionId ? { ...s, internalNotes: editingNotesValue } : s));
          setNotification({ message: 'Notes updated successfully.', type: 'success' });
          setEditingNotesId(null);
          setEditingNotesValue('');
          setPlanLoading(false);
        } else {
          setNotification({ message: response.message || 'Failed to update notes.', type: 'error' });
          setPlanLoading(false);
        }
      })
      .catch(() => {
        setNotification({ message: 'Failed to update notes.', type: 'error' });
        setPlanLoading(false);
      });
  }

  const handleSendLink = () => {
    if (!sendLinkEmail) {
      setNotification({ message: 'Please enter an email address.', type: 'error' });
      return;
    }

    setPlanLoading(true);
    sendSubscriptionLink(sendLinkSubscriptionId, sendLinkEmail)
      .then(response => {
        if (response.success) {
          setNotification({ message: response.message || 'Subscription link sent successfully!', type: 'success' });
          setSendLinkSubscriptionId(null);
          setSendLinkEmail('');
          setPlanLoading(false);
        } else {
          setNotification({ message: response.message || 'Failed to send subscription link.', type: 'error' });
          setPlanLoading(false);
        }
      })
      .catch(() => {
        setNotification({ message: 'Failed to send subscription link.', type: 'error' });
        setPlanLoading(false);
      });
  }

  const handleLoadMore = () => {
    if (!editPlan) return;
    setPlanLoading(true);
    fetchSubscriptions(editPlan.id, subscriptions.length)
      .then((subData) => {
        if (subData.success) {
          setSubscriptions(prevSubs => [...prevSubs, ...subData.subscriptions]);
          setOriginalSubscriptions(prevSubs => [...prevSubs, ...subData.subscriptions]);
        } else {
          setNotification({ message: subData.message || 'Failed to fetch subscriptions.', type: 'error' });
        }
        setPlanLoading(false);
      })
      .catch(() => {
        setNotification({ message: 'Failed to fetch subscriptions.', type: 'error' });
        setPlanLoading(false);
      });
  }

  const handleSort = (field) => {
    let order = 'asc';
    if (sortField === field) {
      order = sortOrder === 'asc' ? 'desc' : (sortOrder === 'desc' ? '' : 'asc');
    }
    setSortField(field);
    setSortOrder(order);

    if (order === '') {
      setSubscriptions(originalSubscriptions);
      return;
    }

    const sortedSubs = [...subscriptions];
    sortedSubs.sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];
      const aEmpty = aValue === null || aValue === undefined || (typeof aValue === 'string' && aValue.trim() === '');
      const bEmpty = bValue === null || bValue === undefined || (typeof bValue === 'string' && bValue.trim() === '');

      if (aEmpty && !bEmpty) return -1;
      if (!aEmpty && bEmpty) return 1;
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
    setSubscriptions(sortedSubs);
  }

  return (
    <Layout full={true}>
      <Notification message={notification.message} onClose={() => setNotification({ message: '', type: 'success' })} type={notification.type} />

      <div className="flex sm:flex-row flex-col justify-between items-center border-b border-accent-main pb-4">
        <h1 className="sm:text-3xl text-2xl font-extrabold text-accent-main flex items-center">
          <CreditCard className="mr-3 w-7 h-7" />
          Plan Management
        </h1>
        {planLoading && (
          <Loading className="h-4 w-4 ml-5" />
        )}
      </div>

      {loading && (
        <div className="text-primary-dark">Loading plans...</div>
      )}

      {!loading && (
        <div className="mt-5 w-full rounded-xl flex sm:flex-row flex-col">
          <div className={`sm:w-1/5 w-full border-accent-main sm:border-r sm:pr-4 sm:block ${editPlan ? 'hidden' : ''}`}>
            <div className="flex flex-row mb-2">
              <h4 className="font-semibold text-lg mb-2 text-primary-dark">Plans</h4>
              <Button className="ml-auto" size="small" onClick={handleAddNew}>Add New</Button>
            </div>
            {plans.length === 0 && <p className="text-primary-dark">No plans found.</p>}
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <ul>
                {plans.map(plan => (
                  <li key={plan.id} className="mb-2">
                    <a
                      href="#"
                      className="block text-accent-main hover:underline border border-accent-main rounded-lg px-3 py-2 transition hover:border-primary-dark bg-background-light flex items-center justify-between"
                      onClick={e => { e.preventDefault(); handleEditPlan(plan); }}
                    >
                      <span>{plan.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className={`flex-1 sm:w-3/5 sm:block sm:pr-4 ml-4 ${editPlan ? '' : 'hidden'}`}>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-bold text-primary-dark">{editPlan ? 'Edit Plan' : 'Add New Plan'}</h3>
              {(isCustomPlan || !editPlan) && (
                <div className="ml-auto flex items-center">
                  <Button onClick={handleSave} loading={planLoading} disabled={planLoading} className="py-0">Save</Button>
                </div>
              )}
            </div>

            <span className="text-sm text-gray-500">Plan ID: {editPlan?.id || '[NEW]'}</span>
            <div className="mt-4">
              <Input required autoFocus type="text" label="Plan Name" value={name} onChange={(e) => setName(e.target.value)} error={nameError} />
              <Input type="textarea" rows={2} label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
              {(isCustomPlan || !editPlan) && (
                <Input type="textarea" rows={2} label="Internal Notes" value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} />
              )}
            </div>
            <div className="flex flex-row mb-2 mt-4">
              <div className="flex">
                <h4 className="font-semibold text-lg my-2 text-primary-dark">Subscriptions</h4>
                <span className="ml-2 text-black bg-red-200 rounded-3xl h-[25px] px-2">{subscriptions.length}</span>
              </div>
              {editPlan && (
              <div className="flex ml-auto gap-2">
                <Button type="primary" size="small" onClick={() => handleEditPlan(editPlan)} className="flex items-center gap-2">
                  <RefreshCcw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
              )}
            </div>
            {isCustomPlan && (
              <div className="flex flex-col sm:flex-row ml-auto gap-2">
                <Input required type="date" label="Start Date" value={startDate} onChange={(e) => setStartDate(e.target.value)} error={startDateError} />
                <Input required type="date" label="End Date" value={endDate} onChange={(e) => setEndDate(e.target.value)} error={endDateError} />
                <Input label="Count" type="number" value={addSubscriptionCount} disabled={planLoading} onChange={(e) => setAddSubscriptionCount(e.target.value)} />
                <Button className="mt-5 mb-3" onClick={handleAddSubscription} loading={planLoading} disabled={planLoading}>Add {addSubscriptionCount}</Button>
              </div>
            )}
            <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="w-full text-sm overflow-hidden border-collapse">
              <thead>
                <tr className="bg-accent-main text-white">
                  <th className="p-2 border border-accent-hover">Id</th>
                  <th className="p-2 border border-accent-hover w-32" onClick={() => handleSort('startDate')}>Start {sortField === 'startDate' ? (sortOrder === 'asc' ? '▲' : sortOrder === 'desc' ? '▼' : '') : ''}</th>
                  <th className="p-2 border border-accent-hover w-32" onClick={() => handleSort('endDate')}>End {sortField === 'endDate' ? (sortOrder === 'asc' ? '▲' : sortOrder === 'desc' ? '▼' : '') : ''}</th>
                  <th className="p-2 border border-accent-hover w-24" onClick={() => handleSort('status')}>Status {sortField === 'status' ? (sortOrder === 'asc' ? '▲' : sortOrder === 'desc' ? '▼' : '') : ''}</th>
                  <th className="p-2 border border-accent-hover w-10">UserId</th>
                  <th className="p-2 border border-accent-hover w-48">Internal Notes</th>
                  {isCustomPlan && (<th className="p-2 border border-accent-hover w-10">Delete</th>)}
                  <th className="p-2 border border-accent-hover w-10">Cancel</th>
                  <th className="p-2 border border-accent-hover w-10">Send</th>
                  <th className="p-2 border border-accent-hover w-10"></th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(sub => (
                  <tr
                    key={sub.id}
                    className={`text-black ${sub.status === 'created' && 'bg-white'} ${sub.status === 'active' && 'bg-green-100'} ${sub.status === 'expired' && 'bg-gray-200'} ${sub.status === 'cancelled' && 'bg-yellow-100'} hover:bg-gray-200`}
                    onMouseEnter={() => setHoveredRowId(sub.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                  >
                    <td className="p-2 border border-accent-hover cursor-pointer" onClick={() => handleCopyId(sub.subscriptionId)}>
                      <div className="flex items-center gap-2">
                        {!isCustomPlan && <a href={`https://dashboard.razorpay.com/app/subscriptions/${sub.subscriptionId}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{sub.subscriptionId}</a>}
                        {isCustomPlan && sub.subscriptionId}

                        <Button
                          type="anchor"
                          className={`text-blue-600 ml-auto ${hoveredRowId === sub.id ? 'opacity-100' : 'opacity-0'}`}
                          size="small"
                          title="Copy Subscription ID"
                        >
                          {copiedId === sub.subscriptionId ? 'copied' : 'copy'}
                        </Button>
                      </div>
                    </td>
                    <td className="p-2 border border-accent-hover text-center">{toDate(sub.startDate)}</td>
                    <td className="p-2 border border-accent-hover text-center">{toDate(sub.endDate)}</td>
                    <td className="p-2 border border-accent-hover text-center">{sub.status}</td>
                    <td className="p-2 border border-accent-hover text-center">{sub.userId}</td>
                    <td className="p-2 border border-accent-hover text-left overflow-wrap break-words max-w-xs">
                      {editingNotesId === sub.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="textarea"
                            value={editingNotesValue}
                            onChange={(e) => setEditingNotesValue(e.target.value)}
                            className=""
                            placeholder="Add notes..."
                            autoFocus
                          />
                          <Button
                            type="primary_outline"
                            size="small"
                            onClick={() => handleSaveNotes(sub.subscriptionId)}
                            disabled={planLoading}
                          >
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button
                            type="secondary_outline"
                            size="small"
                            onClick={() => {
                              setEditingNotesId(null);
                              setEditingNotesValue('');
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm truncate" title={sub.internalNotes}>{sub.internalNotes}</span>
                          <Button
                            type="secondary_outline"
                            size="small"
                            className={`ml-auto ${hoveredRowId === sub.id ? 'opacity-100' : 'opacity-0'}`}
                            onClick={() => {
                              setEditingNotesId(sub.id);
                              setEditingNotesValue(sub.internalNotes || '');
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                    {isCustomPlan && (
                      <td className="p-2 border border-accent-hover text-center">
                        {sub.status === null && (
                          <Button
                            type="danger_outline"
                            size="small"
                            className="min-w-2"
                            title="Delete Subscription"
                            onClick={() => handleDeleteSubscription(sub.subscriptionId)}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    )}
                    <td className="p-2 border border-accent-hover text-center">
                      {sub.status === 'active' && (
                        <Button
                          type="danger"
                          size="small"
                          title="Cancel Subscription"
                          onClick={() => handleCancelSubscription(sub.subscriptionId)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                    <td className="p-2 border border-accent-hover text-center">
                      {!sub.status && (
                        <Button
                          type="primary_outline"
                          size="small"
                          title="Send subscription link to email"
                          onClick={() => setSendLinkSubscriptionId(sub.subscriptionId)}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                    <td className="p-2 border border-accent-hover text-center" title={sub.notes ? JSON.stringify(sub.notes) : ''}>
                      {sub.notes && (
                        <Button type="dark_outline" size="small" onClick={() => setJsonText(JSON.stringify(sub.notes, null, 2))}>
                          <NotepadText className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <Button type="anchor" className="mt-4 ml-auto" onClick={handleLoadMore} disabled={planLoading}>Load More</Button>
          </div>
        </div>
      )}
      <JsonDetailsModal
        open={jsonText}
        onClose={() => setJsonText('')}
        jsonText={jsonText}
      />
      
      <Modal
        open={sendLinkSubscriptionId !== null}
        onClose={() => {
          setSendLinkSubscriptionId(null);
          setSendLinkEmail('');
        }}
        icon={<Send />}
        title="Send Subscription Link"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            Enter the user's email address to send them the subscription activation link.
          </p>
          <Input
            type="email"
            label="Email Address"
            value={sendLinkEmail}
            onChange={(e) => setSendLinkEmail(e.target.value)}
            placeholder="user@example.com"
            required
            autoFocus
          />
          <div className="flex gap-2 justify-end mt-4">
            <Button
              type="secondary"
              onClick={() => {
                setSendLinkSubscriptionId(null);
                setSendLinkEmail('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSendLink}
              loading={planLoading}
              disabled={planLoading || !sendLinkEmail}
            >
              Send Link
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}

